/**
 * LEVEL PROGRESS BAR
 *
 * Displays user's current level, XP progress, and progress to next level
 * with animated progress bar and visual feedback
 */

import React, { useEffect, useState } from 'react';
import { TrendingUp, Zap } from 'lucide-react';
import { LEVELS, getLevelByXP } from '../../constants/gamification';

interface LevelProgressBarProps {
  currentXP: number;
  currentLevel: number;
  levelName: string;
  animated?: boolean;
  showDetails?: boolean;
  className?: string;
}

const LevelProgressBar: React.FC<LevelProgressBarProps> = ({
  currentXP,
  currentLevel,
  levelName,
  animated = true,
  showDetails = true,
  className = ''
}) => {
  const [displayXP, setDisplayXP] = useState(0);

  const levelInfo = LEVELS.find(l => l.level === currentLevel) || LEVELS[0];
  const nextLevel = LEVELS.find(l => l.level === currentLevel + 1);

  // Calculate progress percentage
  const xpInCurrentLevel = currentXP - levelInfo.minXP;
  const xpNeededForNextLevel = nextLevel ? nextLevel.minXP - levelInfo.minXP : 1;
  const progressPercentage = Math.min((xpInCurrentLevel / xpNeededForNextLevel) * 100, 100);

  // Animate XP counter
  useEffect(() => {
    if (!animated) {
      setDisplayXP(currentXP);
      return;
    }

    const duration = 1500; // 1.5 seconds
    const steps = 60;
    const increment = (currentXP - displayXP) / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setDisplayXP(currentXP);
        clearInterval(timer);
      } else {
        setDisplayXP(prev => prev + increment);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [currentXP, animated, displayXP]);

  const isNearNextLevel = progressPercentage >= 80;

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="text-4xl">{levelInfo.emoji}</div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Level {currentLevel} {levelName}
            </h3>
            <p className="text-sm text-gray-600">{levelInfo.description}</p>
          </div>
        </div>
        {nextLevel && (
          <div className="text-right">
            <div className="text-2xl opacity-30">{nextLevel.emoji}</div>
            <p className="text-xs text-gray-500 mt-1">Next Level</p>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center space-x-2">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-semibold text-gray-700">
              {Math.floor(displayXP).toLocaleString()} XP
            </span>
          </div>
          {nextLevel && (
            <span className="text-sm text-gray-500">
              {xpNeededForNextLevel - xpInCurrentLevel} XP to Level {nextLevel.level}
            </span>
          )}
        </div>

        {/* Progress bar container */}
        <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden">
          {/* Progress fill */}
          <div
            className={`absolute top-0 left-0 h-full bg-gradient-to-r ${levelInfo.color} transition-all duration-1000 ease-out ${
              isNearNextLevel ? 'animate-pulse-glow' : ''
            }`}
            style={{ width: `${progressPercentage}%` }}
          >
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shine" />
          </div>

          {/* Percentage text */}
          <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">
            {progressPercentage.toFixed(0)}%
          </div>
        </div>
      </div>

      {/* Details */}
      {showDetails && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-semibold text-blue-900">Current Level</span>
            </div>
            <div className="text-xl font-bold text-blue-900">
              {currentLevel}
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Zap className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-semibold text-purple-900">Total XP</span>
            </div>
            <div className="text-xl font-bold text-purple-900">
              {Math.floor(displayXP).toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Near level up message */}
      {isNearNextLevel && nextLevel && (
        <div className="mt-4 bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm font-semibold text-yellow-900 text-center">
            🔥 Almost there! Just {xpNeededForNextLevel - xpInCurrentLevel} XP to reach{' '}
            {nextLevel.name} Level {nextLevel.level}!
          </p>
        </div>
      )}

      <style>{`
        @keyframes shine {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-shine {
          animation: shine 2s infinite;
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 10px rgba(251, 191, 36, 0.5);
          }
          50% {
            box-shadow: 0 0 20px rgba(251, 191, 36, 0.8);
          }
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default LevelProgressBar;
